import { useState, type FormEvent } from "react";
import { Composer } from "@hopper-ai/hts-assist-components";
import { useI18nContext } from "@hopper-b2b/i18n";

import {
  useTrackSubmittedQuery,
  useTrackTappedSuggestionChip,
} from "./useHtsAssistTracking";

interface HtsAssistLobbyEntryProps {
  onStartQuery: (query: string) => void;
  variant: "embedded" | "page";
  /** Greys out the composer and quick actions and takes them out of the tab order. */
  disabled?: boolean;
}

export const HtsAssistLobbyEntry = ({
  onStartQuery,
  variant,
  disabled = false,
}: HtsAssistLobbyEntryProps) => {
  const { t } = useI18nContext();
  const [query, setQuery] = useState("");
  const trackSubmittedQuery = useTrackSubmittedQuery();
  const trackTappedSuggestionChip = useTrackTappedSuggestionChip();

  const quickActions = [
    t("chat.htsAssistLobbyResendBookingDetails"),
    t("chat.htsAssistLobbyCancelBooking"),
    t("chat.htsAssistLobbyBagsAndSeats"),
    t("chat.htsAssistLobbyTalkToPerson"),
  ];

  const submitQuery = (nextQuery: string) => {
    const trimmedQuery = nextQuery.trim();
    if (!trimmedQuery) return;
    onStartQuery(trimmedQuery);
    setQuery("");
  };

  const handleQuickActionClick = (action: string) => {
    trackTappedSuggestionChip(action);
    submitQuery(action);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    trackSubmittedQuery(query);
    submitQuery(query);
  };

  return (
    <section
      className={`hts-assist-lobby-entry hts-assist-lobby-entry--${variant}`}
    >
      <h1 className="hts-assist-lobby-entry__heading">
        {t("chat.htsAssistLobbyGreeting")}
      </h1>

      <Composer.Root
        className="hts-assist-lobby-entry__composer"
        disabled={disabled}
        onSubmit={handleSubmit}
        size="comfortable"
        variant="condensed"
      >
        <Composer.Input
          aria-label={t("chat.htsAssistComposerPlaceholder")}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("chat.htsAssistComposerPlaceholder")}
          value={query}
        />
        {query.trim() ? (
          <Composer.Actions>
            <Composer.ActionSubmit
              aria-label={t("chat.htsAssistLobbySend")}
              state={disabled ? "idle" : "ready"}
              type="submit"
            />
          </Composer.Actions>
        ) : null}
      </Composer.Root>

      <p className="hts-assist-lobby-entry__disclaimer">
        {t("chat.htsAssistDisclaimer")}
      </p>

      <div
        aria-label={t("chat.htsAssistLobbySuggestions")}
        className="hts-assist-lobby-entry__quick-actions"
        role="group"
      >
        {quickActions.map((action) => (
          <button
            className="hts-assist-lobby-entry__quick-action"
            disabled={disabled}
            key={action}
            onClick={() => handleQuickActionClick(action)}
            type="button"
          >
            {action}
          </button>
        ))}
      </div>
    </section>
  );
};
